import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ContourCustomizationSelection } from '../store/contouring/contouring-types';
import { Card, CardBody, CardHeader } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';

import { getBodyPartIcon } from './body-part-icon';
import ModelModalities from '../pages/customization/ModelModalities';
import { Modality, ModalityDirection } from '../store/global-types/customization-types';
import ModelTags from '../pages/customization/ModelTags';
import { useVersionTags, VersionTags } from './VersionTags';

import './model-list.css';

interface ModelListProps {
    items: ContourCustomizationSelection[];
    onItemClick: (item: ContourCustomizationSelection) => void;
    currentSelection: ContourCustomizationSelection | undefined;
    scrollToCustomization?: ContourCustomizationSelection | undefined;
    handleScrolled?: () => void;
    onTagToggle?: (tag: string) => void;
    onVersionTagToggle?: (tag: string) => void;
    onInputModalityToggle?: (modality: Modality) => void;
    onOutputModalityToggle?: (modality: Modality) => void;
}

const ModelList = (props: ModelListProps) => {
    const { items, onItemClick, currentSelection, scrollToCustomization, handleScrolled, onTagToggle, onInputModalityToggle, onOutputModalityToggle } = props;

    return (
        <div className="model-customization-list">
            {items.map(item => (
                <ModelListItem
                    key={item.customizationBaseId}
                    item={item}
                    onItemClick={onItemClick}
                    currentSelection={currentSelection}
                    scrollToCustomization={scrollToCustomization}
                    handleScrolled={handleScrolled}
                    onTagToggle={onTagToggle}
                    onInputModalityToggle={onInputModalityToggle}
                    onOutputModalityToggle={onOutputModalityToggle}
                />
            ))}
        </div>
    );
}


interface ModelListItemProps {
    item: ContourCustomizationSelection;
    onItemClick: (item: ContourCustomizationSelection) => void;
    currentSelection: ContourCustomizationSelection | undefined;
    scrollToCustomization?: ContourCustomizationSelection | undefined;
    handleScrolled?: () => void;
    onTagToggle?: (tag: string) => void,
    onVersionTagToggle?: (tag: string) => void,
    onInputModalityToggle?: (modality: Modality) => void;
    onOutputModalityToggle?: (modality: Modality) => void;
}

const ModelListItem = (props: ModelListItemProps) => {
    const { item, onItemClick, currentSelection, scrollToCustomization, handleScrolled, onTagToggle, onInputModalityToggle, onOutputModalityToggle, onVersionTagToggle } = props;

    const { t } = useTranslation();

    const ref = useRef<HTMLDivElement | null>(null);

    // scroll to this item if it matches "scrollToCustomization"
    useEffect(() => {
        if (handleScrolled && ref && ref.current && scrollToCustomization && scrollToCustomization.customizationBaseId === item.customizationBaseId) {
            ref.current.scrollIntoView({ behavior: 'auto', block: 'center' });
            handleScrolled();
        }

    }, [item, ref, scrollToCustomization, handleScrolled]);

    const handleTagToggled = useCallback((tag: string) => {
        if (onTagToggle) {
            onTagToggle(tag);
        }
    }, [onTagToggle]);

    const handleVersionTagToggled = useCallback((tag: string) => {
        if (onVersionTagToggle) {
            onVersionTagToggle(tag);
        }
    }, [onVersionTagToggle]);

    const handleInputModalityToggled = useCallback((modality: Modality) => {
        if (onInputModalityToggle) {
            onInputModalityToggle(modality);
        }
    }, [onInputModalityToggle]);

    const handleOutputModalityToggled = useCallback((modality: Modality) => {
        if (onOutputModalityToggle) {
            onOutputModalityToggle(modality);
        }
    }, [onOutputModalityToggle]);

    // Memoize computed values to prevent unnecessary re-renders
    const modelVersionTags = useVersionTags(item.version, item.isLatest);

    const showModalities = item.inputModalities.length > 0 || item.outputModalities.length > 0;
    const showTags = item.tags.length > 0;
    const showVersion = modelVersionTags.length > 0;

    return (
        <div ref={ref} data-testid="contour-model-list-item">
            <Card
                isSelectable
                isSelected={currentSelection?.customizationBaseId === item.customizationBaseId}
                className="model-list-item"
            >
                <CardHeader
                    selectableActions={{
                        selectableActionId: item.customizationBaseId,
                        name: item.customizationLabel,
                        onChange: () => onItemClick(item),
                        hasNoOffset: true,
                    }}>
                    <div className="model-select-item">
                        <span className="body-part-icon">{getBodyPartIcon(item.bodyPart)}</span>
                        <span>{item.customizationLabel}</span>
                    </div>
                </CardHeader>
                <CardBody>
                    <div className="model-item-body">

                        {showModalities && (
                            <>
                                <div>
                                    {item.inputModalities.length > 0 && (
                                        <span data-testid="model-list-item-input-modalities">
                                            <ModelModalities
                                                modalities={item.inputModalities}
                                                direction={ModalityDirection.Input}
                                                label={t('customizationPage.modelModalities.inputModalities', { count: item.inputModalities.length })}
                                                onToggle={handleInputModalityToggled}
                                            />
                                        </span>
                                    )}
                                </div><div>
                                    {item.outputModalities.length > 0 && (
                                        <span data-testid="model-list-item-output-modalities">
                                            <ModelModalities
                                                modalities={item.outputModalities}
                                                direction={ModalityDirection.Output}
                                                label={t('customizationPage.modelModalities.outputModalities', { count: item.outputModalities.length })}
                                                onToggle={handleOutputModalityToggled}
                                            />
                                        </span>
                                    )}
                                </div>
                            </>
                        )}

                        {showVersion && (
                            <div className="model-list-version-tags">
                                <VersionTags
                                    tags={modelVersionTags}
                                    onToggle={handleVersionTagToggled}
                                    allowInteraction={true}
                                />
                            </div>
                        )}

                        {showTags && (
                            <div className="model-list-tags">
                                <ModelTags tags={item.tags} onToggle={handleTagToggled} />
                            </div>
                        )}

                        <div>{t('modelList.action')}: {item.action}</div>

                    </div>

                </CardBody>
            </Card>
        </div>
    );
}

export default ModelList;
